/**
 * PIP-343 — outbound hosts known to be dead for EVERY page they serve.
 *
 * A CTA must never send a user off-platform to a page we already know is gone.
 * The PIP-163 link checker covers individual URLs; this covers whole hosts
 * that were retired wholesale, so no per-URL check is needed (or possible —
 * some, like Ticketmaster, block headless requests).
 *
 * One list, one helper: the TM ingest lane uses it to refuse a dead
 * `venueBoxOffice` outlet, and the CTA derivation uses it to skip a dead
 * candidate on rows persisted before the guard existed.
 *
 * Add a host only when the WHOLE host is dead, with the evidence beside it.
 */

/**
 * Apex domains whose every page is dead. Matched as the apex or any
 * subdomain, never as a suffix look-alike.
 *
 * - `business.site` — Google Business Profile websites. Google shut every one
 *   down in 2024 (redirect, then 404). TM still publishes them as
 *   `venueBoxOffice` outlets: `https://blackboxclt.business.site/` returned
 *   HTTP 404 on 2026-10-03.
 */
export const KNOWN_DEAD_OUTBOUND_HOST_APEXES: readonly string[] = ['business.site'];

/** Whether `url` is on a host in {@link KNOWN_DEAD_OUTBOUND_HOST_APEXES}. */
export function isKnownDeadOutboundUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  let host: string;
  try {
    host = new URL(url).hostname.toLowerCase();
  } catch {
    return false;
  }
  return KNOWN_DEAD_OUTBOUND_HOST_APEXES.some((apex) => host === apex || host.endsWith(`.${apex}`));
}
